/* eslint-disable react/display-name */
import { type ComponentType, memo, useState } from 'react';

import { Datepicker, Number, Text } from '../../Form';

export interface ChangeComponentProps {
  value: unknown;
}

type ChangeComponentType = ComponentType<ChangeComponentProps>;

interface BaseWrapperProps {
  label: string;
  name: string;
  description?: string;
  isFormula: boolean;
  ChangeComponent?: ChangeComponentType;
}

export interface TextWrapperProps extends BaseWrapperProps {
  readonly?: boolean;
}

interface TextMemoProps extends TextWrapperProps {
  onBlur: (value: string) => void;
}

const TextMemoComponent = memo(({ readonly, label, name, description, onBlur }: TextMemoProps) => (
  <Text readonly={readonly} label={label} name={name} description={description} onBlur={onBlur} />
));

export const TextWrapper = ({
  readonly,
  label,
  name,
  description,
  isFormula,
  ChangeComponent
}: TextWrapperProps) => {
  const [cellValue, setCellValue] = useState<string | undefined>(undefined);

  return (
    <>
      {!isFormula && ChangeComponent ? <ChangeComponent value={cellValue} /> : null}
      <TextMemoComponent
        readonly={readonly}
        label={label}
        name={name}
        description={description}
        isFormula={isFormula}
        ChangeComponent={ChangeComponent}
        onBlur={setCellValue}
      />
    </>
  );
};

export interface NumberWrapperProps extends BaseWrapperProps {
  readOnly?: boolean;
  allowDecimal?: boolean;
  includeThousandsSeparator?: boolean;
  allowNegative?: boolean;
}

interface NumberMemoProps extends NumberWrapperProps {
  onBlur: (value: number) => void;
}

const NumberMemoComponent = memo(
  ({
    label,
    name,
    description,
    readOnly,
    onBlur,
    allowDecimal = false,
    includeThousandsSeparator = false,
    allowNegative = false
  }: NumberMemoProps) => (
    <Number
      readonly={readOnly}
      label={label}
      name={name}
      description={description}
      includeThousandsSeparator={includeThousandsSeparator}
      allowDecimal={allowDecimal}
      allowNegative={allowNegative}
      onBlur={onBlur}
    />
  )
);

export const NumberWrapper = ({
  readOnly,
  label,
  name,
  description,
  isFormula,
  allowDecimal,
  includeThousandsSeparator,
  allowNegative,
  ChangeComponent
}: NumberWrapperProps) => {
  const [cellValue, setCellValue] = useState<number | undefined>(undefined);

  return (
    <>
      {!isFormula && ChangeComponent ? <ChangeComponent value={cellValue} /> : null}
      <NumberMemoComponent
        readOnly={readOnly}
        label={label}
        name={name}
        description={description}
        isFormula={isFormula}
        allowDecimal={allowDecimal}
        includeThousandsSeparator={includeThousandsSeparator}
        allowNegative={allowNegative}
        ChangeComponent={ChangeComponent}
        onBlur={setCellValue}
      />
    </>
  );
};

export interface DateComponentProps extends BaseWrapperProps {
  readOnly?: boolean;
  defaultValue?: Date;
}

interface DateMemoProps extends DateComponentProps {
  onBlur: (value: Date | [Date, Date] | null) => void;
}

const DateMemoComponent = memo(
  ({ readOnly, label, name, description, onBlur, defaultValue }: DateMemoProps) => (
    <Datepicker
      readOnly={readOnly}
      label={label}
      onBlur={onBlur}
      name={name}
      description={description}
      defaultValue={defaultValue}
    />
  )
);

export const DateComponent = ({
  readOnly,
  label,
  name,
  description,
  defaultValue,
  isFormula,
  ChangeComponent
}: DateComponentProps) => {
  const [cellValue, setCellValue] = useState<Date | [Date, Date] | null | undefined>(undefined);

  return (
    <>
      {!isFormula && ChangeComponent ? <ChangeComponent value={cellValue} /> : null}
      <DateMemoComponent
        readOnly={readOnly}
        label={label}
        name={name}
        description={description}
        defaultValue={defaultValue}
        isFormula={isFormula}
        ChangeComponent={ChangeComponent}
        onBlur={setCellValue}
      />
    </>
  );
};
